/**
 * The airports reference in the browser (SA-1031 spec §7.1, D11).
 *
 * `useAirportLookup` is one React Query read per distinct sorted code set with
 * a day-long cache: a render's codes become one `resolve` call, and a failed
 * read answers an EMPTY map — a display site then prints what it printed
 * before this item, never an error line. Core, so a module's components may
 * call it (custom → core is the allowed direction).
 *
 * The airports reference is the aviation kit's (SA-1366): every read here is
 * strict on the LOADED `aviation` flag (`useAirportsAvailable`), so a
 * deployment without the kit sends nothing the server would 404 and a display
 * site prints the text it was given.
 */
import { useMemo } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { resolveAirports, searchAirports } from '@/services/airports'
import { useFeatureStore } from '@/stores/featureStore'
import type { Airport } from '@/types/airports'
import { useDebouncedValue } from './useDebouncedValue'

// Re-exported for callers that imported it from here before it had its own file (SA-1056).
export { useDebouncedValue }

export const airportsKey = ['airports'] as const
/** The endpoint's ceiling (`RESOLVE_MAX`); a register page is at most 100 rows, two codes each. */
export const AIRPORT_RESOLVE_LIMIT = 200
const DAY_MS = 24 * 60 * 60 * 1000
const EMPTY: Record<string, Airport> = Object.freeze({}) as Record<string, Airport>

/** SA-1366: the airports routes exist only under the aviation kit; strict, so nothing is asked before the manifest says so. */
export function useAirportsAvailable(): boolean {
  return useFeatureStore((s) => s.isLoaded && s.features.has('aviation'))
}

export function useAirportLookup(codes: string[]): { byCode: Record<string, Airport>; loading: boolean } {
  const key = codes.map((c) => c.trim().toUpperCase()).filter(Boolean).join('\u0000')
  const distinct = useMemo(() => Array.from(new Set(key ? key.split('\u0000') : [])).sort().slice(0, AIRPORT_RESOLVE_LIMIT), [key])
  const asking = useAirportsAvailable() && distinct.length > 0
  const query = useQuery({
    queryKey: [...airportsKey, 'resolve', distinct.join(',')],
    enabled: asking,
    staleTime: DAY_MS,
    gcTime: DAY_MS,
    retry: false,
    queryFn: () => resolveAirports(distinct),
  })
  return { byCode: query.data ?? EMPTY, loading: asking && query.isPending }
}

export const SEARCH_MIN = 2
export const SEARCH_DEBOUNCE_MS = 250

export function useAirportSearch(
  q: string, { iataOnly = false, enabled = true }: { iataOnly?: boolean; enabled?: boolean } = {},
): UseQueryResult<Airport[], Error> & { asked: string; unavailable: boolean } {
  const text = useDebouncedValue(q.trim(), SEARCH_DEBOUNCE_MS)
  const available = useAirportsAvailable()
  const query = useQuery({
    queryKey: [...airportsKey, 'search', text, iataOnly],
    enabled: available && enabled && text.length >= SEARCH_MIN,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: () => searchAirports(text, { limit: 5, iataOnly }),
  })
  // `asked` is the debounced text the query is keyed on, so a caller can tell "still debouncing" from "answered".
  // `unavailable` (SA-1366): no airports reference here, so nothing can be checked; a caller that lets a
  // well-formed code through when a search fails ("Couldn't check") does the same.
  return { ...query, asked: text, unavailable: !available }
}
